/**
 * Global Site Entrance Loader
 * Premium loading experience with mouse reactivity
 * Auto-hides when page is fully loaded
 */

class SiteLoader {
  constructor() {
    this.loader = null;
    this.progressFill = null;
    this.progressGlow = null;
    this.percentage = null;
    this.cursor = null;
    this.canvas = null;
    this.ctx = null;

    // Loading state
    this.progress = 0;
    this.targetProgress = 0;
    this.isComplete = false;

    // Mouse tracking
    this.mouseX = 0;
    this.mouseY = 0;

    // Resource tracking
    this.resourcesLoaded = 0;
    this.totalResources = 0;

    // Settings (can be customized via data attributes)
    this.minDisplayTime = 1000; // Minimum time to show loader (ms)
    this.enableMouseReactive = true;
    this.enableParticles = true;
    this.autoHide = true;

    this.init();
  }

  init() {
    // Check if loader should be enabled
    if (sessionStorage.getItem('siteLoaderShown') === 'true' && !window.Shopify?.designMode) {
      // Don't show loader on subsequent page loads in same session
      this.removeLoader();
      return;
    }

    this.createLoader();
    this.setupEventListeners();
    this.trackResources();
    this.startProgressAnimation();

    if (this.enableParticles) {
      this.initParticles();
    }

    // Start loading
    this.simulateProgress();

    // Mark that loader has been shown in this session
    sessionStorage.setItem('siteLoaderShown', 'true');
  }

  createLoader() {
    // Check if loader already exists
    if (document.getElementById('siteLoader')) {
      this.loader = document.getElementById('siteLoader');
      this.bindElements();
      return;
    }

    // Create loader HTML
    const loaderHTML = `
      <div id="siteLoader">
        <div class="loader-particles">
          <canvas id="loaderParticles"></canvas>
        </div>
        <div class="loader-cursor"></div>
        <div class="loader-content">
          <div class="loader-logo">
            ${this.getLogoHTML()}
          </div>
          <div class="loader-progress-wrapper">
            <span class="loader-text">Loading Experience</span>
            <span class="loader-percentage">0%</span>
            <div class="loader-bar-container">
              <div class="loader-bar-fill"></div>
              <div class="loader-bar-glow"></div>
            </div>
          </div>
          <div class="loader-subtext">
            Please wait<span class="loader-dots"></span>
          </div>
        </div>
      </div>
    `;

    // Insert loader at the beginning of body
    document.body.insertAdjacentHTML('afterbegin', loaderHTML);

    this.bindElements();
  }

  bindElements() {
    this.loader = document.getElementById('siteLoader');
    this.progressFill = this.loader.querySelector('.loader-bar-fill');
    this.progressGlow = this.loader.querySelector('.loader-bar-glow');
    this.percentage = this.loader.querySelector('.loader-percentage');
    this.cursor = this.loader.querySelector('.loader-cursor');
    this.canvas = this.loader.querySelector('#loaderParticles');

    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d');
    }
  }

  getLogoHTML() {
    // Try to get logo from theme settings or use placeholder
    const logoUrl = window.siteLoaderConfig?.logoUrl;

    if (logoUrl) {
      return `<img src="${logoUrl}" alt="Loading">`;
    }

    // SVG placeholder logo
    return `
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="2"/>
        <circle cx="50" cy="50" r="35" fill="rgba(109, 56, 139, 0.2)"/>
        <path d="M 50 20 L 65 50 L 50 80 L 35 50 Z" fill="rgba(255,255,255,0.8)"/>
      </svg>
    `;
  }

  setupEventListeners() {
    // Mouse tracking for reactive effects
    if (this.enableMouseReactive) {
      document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    }

    // Touch tracking for mobile
    document.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });

    // Page load complete
    window.addEventListener('load', () => this.onPageLoad());

    // Handle browser back/forward
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.removeLoader();
      }
    });
  }

  handleMouseMove(e) {
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    // Update cursor position
    if (this.cursor) {
      this.cursor.style.transform = `translate(${e.clientX - 20}px, ${e.clientY - 20}px)`;
    }

    // Mouse position affects progress (subtle effect)
    if (!this.isComplete && this.enableMouseReactive) {
      const rect = this.loader.getBoundingClientRect();
      const mouseProgress = (e.clientX / rect.width) * 10; // 0-10% bonus from mouse
      this.targetProgress = Math.min(this.targetProgress + mouseProgress * 0.01, 100);
    }
  }

  handleTouchMove(e) {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      this.mouseX = touch.clientX;
      this.mouseY = touch.clientY;
    }
  }

  trackResources() {
    // Count all resources that need to load
    const images = document.querySelectorAll('img[src]');
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    const scripts = document.querySelectorAll('script[src]');

    this.totalResources = images.length + stylesheets.length + scripts.length;

    // Track image loading
    images.forEach(img => {
      if (img.complete) {
        this.resourcesLoaded++;
      } else {
        img.addEventListener('load', () => this.onResourceLoad());
        img.addEventListener('error', () => this.onResourceLoad());
      }
    });

    // Track stylesheet loading
    stylesheets.forEach(link => {
      link.addEventListener('load', () => this.onResourceLoad());
      link.addEventListener('error', () => this.onResourceLoad());
    });

    // Track script loading
    scripts.forEach(script => {
      script.addEventListener('load', () => this.onResourceLoad());
      script.addEventListener('error', () => this.onResourceLoad());
    });
  }

  onResourceLoad() {
    this.resourcesLoaded++;

    // Update target progress based on resources loaded
    const resourceProgress = (this.resourcesLoaded / this.totalResources) * 100;
    this.targetProgress = Math.max(this.targetProgress, resourceProgress);
  }

  simulateProgress() {
    // Simulate initial loading progress
    const intervals = [
      { delay: 100, progress: 15 },
      { delay: 300, progress: 30 },
      { delay: 600, progress: 50 },
      { delay: 1000, progress: 70 },
      { delay: 1500, progress: 85 }
    ];

    intervals.forEach(({ delay, progress }) => {
      setTimeout(() => {
        if (!this.isComplete) {
          this.targetProgress = Math.max(this.targetProgress, progress);
        }
      }, delay);
    });
  }

  startProgressAnimation() {
    const animate = () => {
      // Smooth progress interpolation
      const diff = this.targetProgress - this.progress;
      this.progress += diff * 0.1;

      // Update UI
      if (this.progressFill) {
        this.progressFill.style.width = `${this.progress}%`;
      }

      if (this.percentage) {
        this.percentage.textContent = `${Math.floor(this.progress)}%`;
      }

      if (this.progressGlow) {
        this.progressGlow.style.right = `${100 - this.progress}%`;
      }

      // Continue animation
      if (!this.isComplete || this.progress < 99.9) {
        requestAnimationFrame(animate);
      } else {
        this.completeLoading();
      }
    };

    animate();
  }

  onPageLoad() {
    // Ensure minimum display time
    const loadTime = performance.now();
    const remainingTime = Math.max(0, this.minDisplayTime - loadTime);

    setTimeout(() => {
      this.targetProgress = 100;
      this.isComplete = true;
    }, remainingTime);
  }

  completeLoading() {
    // Add loaded class for exit animation
    if (this.loader) {
      const container = this.loader.querySelector('.loader-bar-container');
      if (container) {
        container.classList.add('loading-complete');
      }
    }

    // Hide loader after animation
    setTimeout(() => {
      this.hideLoader();
    }, 500);
  }

  hideLoader() {
    if (!this.loader) return;

    this.loader.classList.add('loaded');

    // Remove from DOM after transition
    setTimeout(() => {
      this.removeLoader();
    }, 800);
  }

  removeLoader() {
    if (this.loader && this.loader.parentNode) {
      this.loader.classList.add('hidden');
      // Keep in DOM but hidden for potential debugging
      // this.loader.parentNode.removeChild(this.loader);
    }
  }

  /* ============================================
     Particle System
     ============================================ */

  initParticles() {
    if (!this.canvas || !this.ctx) return;

    // Set canvas size
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());

    // Create particles
    this.particles = [];
    const particleCount = window.innerWidth < 768 ? 30 : 50;

    for (let i = 0; i < particleCount; i++) {
      this.particles.push(new Particle(this.canvas.width, this.canvas.height));
    }

    // Start animation
    this.animateParticles();
  }

  resizeCanvas() {
    if (!this.canvas) return;
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  animateParticles() {
    if (!this.ctx || !this.canvas || this.isComplete) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Update and draw particles
    this.particles.forEach(particle => {
      particle.update(this.canvas.width, this.canvas.height, this.mouseX, this.mouseY);
      particle.draw(this.ctx);
    });

    // Draw connections
    this.particles.forEach((p1, i) => {
      this.particles.slice(i + 1).forEach(p2 => {
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          this.ctx.strokeStyle = `rgba(109, 56, 139, ${0.2 * (1 - distance / 150)})`;
          this.ctx.lineWidth = 1;
          this.ctx.beginPath();
          this.ctx.moveTo(p1.x, p1.y);
          this.ctx.lineTo(p2.x, p2.y);
          this.ctx.stroke();
        }
      });
    });

    requestAnimationFrame(() => this.animateParticles());
  }
}

/* ============================================
   Particle Class
   ============================================ */

class Particle {
  constructor(canvasWidth, canvasHeight) {
    this.reset(canvasWidth, canvasHeight);
  }

  reset(canvasWidth, canvasHeight) {
    this.x = Math.random() * canvasWidth;
    this.y = Math.random() * canvasHeight;
    this.vx = (Math.random() * 2 - 1) * 0.5;
    this.vy = (Math.random() * 2 - 1) * 0.5;
    this.size = Math.random() * 2 + 1;
    this.opacity = Math.random() * 0.5 + 0.2;
  }

  update(canvasWidth, canvasHeight, mouseX, mouseY) {
    // Move particle
    this.x += this.vx;
    this.y += this.vy;

    // Mouse repulsion
    if (mouseX && mouseY) {
      const dx = this.x - mouseX;
      const dy = this.y - mouseY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 100) {
        const force = (100 - distance) / 100;
        this.vx += (dx / distance) * force * 0.2;
        this.vy += (dy / distance) * force * 0.2;
      }
    }

    // Damping
    this.vx *= 0.99;
    this.vy *= 0.99;

    // Wrap around edges
    if (this.x < 0) this.x = canvasWidth;
    if (this.x > canvasWidth) this.x = 0;
    if (this.y < 0) this.y = canvasHeight;
    if (this.y > canvasHeight) this.y = 0;

    // Constrain velocity
    const maxSpeed = 2;
    const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    if (speed > maxSpeed) {
      this.vx = (this.vx / speed) * maxSpeed;
      this.vy = (this.vy / speed) * maxSpeed;
    }
  }

  draw(ctx) {
    ctx.fillStyle = `rgba(109, 56, 139, ${this.opacity})`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

/* ============================================
   Initialize on DOM Ready
   ============================================ */

// Initialize loader as early as possible
if (document.readyState === 'loading') {
  // Create loader immediately
  const earlyLoader = new SiteLoader();
} else {
  // DOM already loaded
  const earlyLoader = new SiteLoader();
}

// Export for potential manual control
window.SiteLoader = SiteLoader;
